"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "RelationManager", {
    enumerable: true,
    get: function() {
        return RelationManager;
    }
});
const _common = require("@nestjs/common");
const _nocodbsdk = require("nocodb-sdk");
const _addremovelinks = require("./BaseModelSqlv2/add-remove-links");
const _models = require("~/models");
const _relationupdatewebhookhandler = require("~/db/relation-update-webhook-handler");
const _catchError = require("~/helpers/catchError");
const _dbHelpers = require("~/helpers/dbHelpers");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const logger = new _common.Logger('RelationManager');
let RelationManager = class RelationManager {
    getRelationContext() {
        return this.relationContext;
    }
    // for M2M and Belongs to relation, the relation stored in column option is reversed
    // parent become child, child become parent from the viewpoint of col options
    // In v2 we use junction table and it act like MM so we treat it as reversed relation as well
    static isRelationReversed(relationColumn, colOptions) {
        var _relationColumn_meta;
        const isBelongsTo = colOptions.type === _nocodbsdk.RelationTypes.BELONGS_TO || ((_relationColumn_meta = relationColumn.meta) == null ? void 0 : _relationColumn_meta.bt);
        const reversed = (0, _nocodbsdk.isLinkV2)(relationColumn) || isBelongsTo || colOptions.type === _nocodbsdk.RelationTypes.MANY_TO_MANY;
        return reversed;
    }
    static async getRelationManager(baseModel, colId, id) {
        await baseModel.model.getColumns(baseModel.context);
        const column = baseModel.model.columnsById[colId];
        if (!column || !(0, _nocodbsdk.isLinksOrLTAR)(column.uidt)) _catchError.NcError.get(baseModel.context).fieldNotFound(colId);
        const colOptions = await column.getColOptions(baseModel.context);
        const { parentContext, childContext, refContext, mmContext } = await colOptions.getParentChildContext(baseModel.context);
        const childColumn = await colOptions.getChildColumn(childContext);
        const parentColumn = await colOptions.getParentColumn(parentContext);
        const parentTable = await parentColumn.getModel(parentContext);
        const childTable = await childColumn.getModel(childContext);
        await childTable.getColumns(childContext);
        await parentTable.getColumns(parentContext);
        const parentBaseModel = await _models.Model.getBaseModelSQL(parentContext, {
            model: parentTable,
            dbDriver: baseModel.dbDriver
        });
        const childBaseModel = await _models.Model.getBaseModelSQL(childContext, {
            dbDriver: baseModel.dbDriver,
            model: childTable
        });
        return new RelationManager({
            baseModel,
            relationColumn: column,
            relationColOptions: colOptions,
            childTn: childBaseModel.getTnPath(childTable),
            childColumn,
            childTable,
            childBaseModel,
            parentTn: parentBaseModel.getTnPath(parentTable),
            parentColumn,
            parentTable,
            parentBaseModel,
            childId: // in bt or mm child id and row id is swapped
            // due to table definition
            RelationManager.isRelationReversed(column, colOptions) ? id.rowId : id.childId,
            parentId: RelationManager.isRelationReversed(column, colOptions) ? id.childId : id.rowId,
            parentContext,
            childContext,
            refContext,
            mmContext
        });
    }
    async getHmOrOoChildRow() {
        const { childBaseModel: baseModel, childTn, childColumn, childTable, childId } = this.relationContext;
        return await baseModel.execAndParse(baseModel.dbDriver(childTn).select(...new Set([
            childColumn,
            ...childTable.primaryKeys
        ].map((col)=>`${childTable.table_name}.${col.column_name}`))).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)), null, {
            raw: true,
            first: true
        });
    }
    /**
   * Get related rows through the junction table for v2 links.
   * Returns only PK columns from the parent table (not SELECT *).
   * F4 fix: select only primary keys instead of all columns.
   */ async getLinkV2RelatedRowPks() {
        const { childTable, childBaseModel, childTn, childColumn, childId, parentBaseModel, parentTn, parentColumn, parentTable, relationColOptions, mmContext } = this.relationContext;
        const vChildCol = await relationColOptions.getMMChildColumn(mmContext);
        const vParentCol = await relationColOptions.getMMParentColumn(mmContext);
        const vTable = await relationColOptions.getMMModel(mmContext);
        const assocBaseModel = await _models.Model.getBaseModelSQL(mmContext, {
            model: vTable,
            dbDriver: childBaseModel.dbDriver
        });
        const vTn = assocBaseModel.getTnPath(vTable);
        // F4: Select only PK columns instead of parentTn.*
        const pkColumns = parentTable.primaryKeys.map((pk)=>`${parentTn}.${pk.column_name}`);
        return await parentBaseModel.execAndParse(parentBaseModel.dbDriver(parentTn).select(...pkColumns).join(vTn, `${vTn}.${vParentCol.column_name}`, `${parentTn}.${parentColumn.column_name}`).where({
            [`${vTn}.${vChildCol.column_name}`]: childBaseModel.dbDriver(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first()
        }), null, {
            raw: true
        });
    }
    async getHmOrOoChildLinkedWithParent() {
        const { childBaseModel: baseModel, childTn, parentTn, childColumn, parentColumn, parentTable, parentId } = this.relationContext;
        return await baseModel.execAndParse(baseModel.dbDriver(childTn).where({
            [childColumn.column_name]: baseModel.dbDriver.from(baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first().as('___cn_alias'))
        }), null, {
            raw: true,
            first: true
        });
    }
    /**
   * Resolve junction table metadata shared across v2 operations.
   * F5 fix: resolve once, reuse across operations instead of per-row.
   */ async resolveJunctionMeta() {
        const { relationColOptions: colOptions, baseModel, mmContext } = this.relationContext;
        const vChildCol = await colOptions.getMMChildColumn(mmContext);
        const vParentCol = await colOptions.getMMParentColumn(mmContext);
        const vTable = await colOptions.getMMModel(mmContext);
        const assocBaseModel = await _models.Model.getBaseModelSQL(mmContext, {
            model: vTable,
            dbDriver: baseModel.dbDriver
        });
        const vTn = assocBaseModel.getTnPath(vTable);
        return {
            vChildCol,
            vParentCol,
            vTable,
            assocBaseModel,
            vTn
        };
    }
    /**
   * Batch delete junction rows by a filter column value (using a subquery).
   * Returns the FK pairs that were deleted for audit log generation.
   *
   * F3 fix: single SELECT + single DELETE instead of N removeChild calls.
   */ async batchDeleteJunctionRows(trx, params) {
        const { vTn, vChildCol, vParentCol, filterColName, filterSubquery } = params;
        // 1. SELECT existing junction rows (just FK columns)
        const existingRows = await trx(vTn).select(vChildCol.column_name, vParentCol.column_name).where(filterColName, filterSubquery);
        if (existingRows.length === 0) {
            return [];
        }
        // 2. Batch DELETE all matching junction rows
        await trx(vTn).where(filterColName, filterSubquery).delete();
        return existingRows.map((row)=>({
                childFk: row[vChildCol.column_name],
                parentFk: row[vParentCol.column_name]
            }));
    }
    /**
   * Generate unlink audit log entries for removed junction rows.
   * Produces the same audit entries that individual removeChild calls would.
   */ async generateUnlinkAuditLogs(removedPairs, relationType, parentTable, childTable) {
        if (!removedPairs.length) return;
        const { baseModel } = this.relationContext;
        // Batch-fetch display values for all evicted rows
        const displayValues = await baseModel.readOnlyPrimariesByPkFromModel(removedPairs.flatMap((pair)=>[
                {
                    model: parentTable,
                    id: pair.parentFk
                },
                {
                    model: childTable,
                    id: pair.childFk
                }
            ]));
        for(let i = 0; i < removedPairs.length; i++){
            const pair = removedPairs[i];
            const parentDisplayValue = displayValues[i * 2];
            const childDisplayValue = displayValues[i * 2 + 1];
            this.auditUpdateObj.push({
                rowId: pair.parentFk,
                refRowId: pair.childFk,
                displayValue: parentDisplayValue,
                refDisplayValue: childDisplayValue,
                opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                type: relationType,
                direction: 'parent_child'
            });
            if (parentTable.id !== childTable.id) {
                this.auditUpdateObj.push({
                    rowId: pair.childFk,
                    refRowId: pair.parentFk,
                    displayValue: childDisplayValue,
                    refDisplayValue: parentDisplayValue,
                    opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                    type: (0, _dbHelpers.getOppositeRelationType)(relationType),
                    direction: 'child_parent'
                });
            }
        }
    }
    /**
   * Enforce v2 cardinality constraints and insert the new link atomically.
   * F1/F2 fix: wraps everything in a single DB transaction.
   * F3/F5 fix: batch delete instead of per-row removeChild.
   * F10 fix: no dummy childId needed.
   * F14 fix: single broadcast after all operations.
   */ async enforceV2CardinalityAndInsert(params) {
        const { relationColOptions: colOptions, baseModel, parentBaseModel, parentColumn, parentTable, parentTn, childBaseModel, childColumn, childTable, childTn, childId, parentId, relationColumn } = this.relationContext;
        const { vChildCol, vParentCol, vTn, column, req, refTableLinkColumnId } = params;
        const allAffectedParentIds = [];
        const allAffectedChildIds = [];
        let moRemovedPairs = [];
        let omRemovedPairs = [];
        // F1/F2: Wrap cardinality enforcement + insert in a single transaction
        const trx = await baseModel.dbDriver.transaction();
        try {
            // ManyToOne / OneToOne: this child can only link to one parent
            // → remove all existing junction rows for this child
            if ([
                'mo',
                'oo'
            ].includes(colOptions.type)) {
                const childFkSubquery = trx(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first();
                moRemovedPairs = await this.batchDeleteJunctionRows(trx, {
                    vTn,
                    vChildCol,
                    vParentCol,
                    filterColName: vChildCol.column_name,
                    filterSubquery: childFkSubquery
                });
                for (const pair of moRemovedPairs){
                    allAffectedParentIds.push(pair.parentFk);
                }
                if (moRemovedPairs.length > 1) {
                    logger.warn(`V2 cardinality enforcement removed ${moRemovedPairs.length} junction rows ` + `(expected 0-1) for child ${childId} on column ${relationColumn.id}`);
                }
            }
            // OneToMany / OneToOne: this parent can only be linked to by one child
            // → remove all existing junction rows for this parent
            if ([
                'om',
                'oo'
            ].includes(colOptions.type)) {
                const parentFkSubquery = trx(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first();
                omRemovedPairs = await this.batchDeleteJunctionRows(trx, {
                    vTn,
                    vChildCol,
                    vParentCol,
                    filterColName: vParentCol.column_name,
                    filterSubquery: parentFkSubquery
                });
                for (const pair of omRemovedPairs){
                    allAffectedChildIds.push(pair.childFk);
                }
                if (omRemovedPairs.length > 1) {
                    logger.warn(`V2 cardinality enforcement removed ${omRemovedPairs.length} junction rows ` + `(expected 0-1) for parent ${parentId} on column ${relationColumn.id}`);
                }
            }
            // Insert the new junction row within the same transaction
            if (baseModel.isSnowflake || baseModel.isDatabricks) {
                const parentPK = trx(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first();
                const childPK = trx(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first();
                await trx.raw(`INSERT INTO ?? (??, ??) SELECT (${parentPK.toQuery()}), (${childPK.toQuery()})`, [
                    vTn,
                    vParentCol.column_name,
                    vChildCol.column_name
                ]);
            } else {
                await trx(vTn).insert({
                    [vParentCol.column_name]: trx(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first(),
                    [vChildCol.column_name]: trx(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first()
                });
            }
            await trx.commit();
        } catch (e) {
            await trx.rollback();
            throw e;
        }
        // Generate audit logs after transaction (needs display value queries)
        if (moRemovedPairs.length) {
            await this.generateUnlinkAuditLogs(moRemovedPairs, colOptions.type, parentTable, childTable);
        }
        if (omRemovedPairs.length) {
            await this.generateUnlinkAuditLogs(omRemovedPairs, colOptions.type, parentTable, childTable);
        }
        // F14: Single batch of updateLastModified and broadcast after transaction
        // Include evicted row IDs so their updated_at is refreshed too
        const allParentRowIds = [
            parentId,
            ...allAffectedParentIds.filter((id)=>id !== parentId)
        ];
        const allChildRowIds = [
            childId,
            ...allAffectedChildIds.filter((id)=>id !== childId)
        ];
        await parentBaseModel.updateLastModified({
            baseModel: parentBaseModel,
            model: parentTable,
            rowIds: allParentRowIds,
            cookie: req,
            updatedColIds: [
                refTableLinkColumnId
            ]
        });
        await childBaseModel.updateLastModified({
            baseModel: childBaseModel,
            model: childTable,
            rowIds: allChildRowIds,
            cookie: req,
            updatedColIds: [
                column.id
            ]
        });
        await parentBaseModel.broadcastLinkUpdates(allParentRowIds);
        await childBaseModel.broadcastLinkUpdates(allChildRowIds);
    }
    async addChild(params) {
        var _this, _req_query;
        const { relationColOptions: colOptions, baseModel, parentBaseModel, parentColumn, parentTable, parentTn, childBaseModel, childColumn, childTable, childTn, childId, parentId, relationColumn } = this.relationContext;
        const column = relationColumn;
        // Get the corresponding link column ID for the parent table
        const refTableLinkColumnId = (_this = await (0, _addremovelinks.extractCorrespondingLinkColumn)(baseModel.context, {
            ltarColumn: column,
            referencedTable: colOptions.fk_related_model_id === parentTable.id ? parentTable : childTable
        })) == null ? void 0 : _this.id;
        const isMMLike = (0, _nocodbsdk.isMMOrMMLike)(this.relationContext.relationColumn);
        const { onlyUpdateAuditLogs, req } = params;
        if (onlyUpdateAuditLogs && colOptions.type !== _nocodbsdk.RelationTypes.BELONGS_TO) {
            return await this.handleOnlyUpdateAudit(params);
        }
        // F12: Create webhook handler before any mutations (captures pre-state)
        const webhookHandler = await _relationupdatewebhookhandler.RelationUpdateWebhookHandler.beginUpdate({
            childBaseModel,
            parentBaseModel,
            user: req.user,
            ignoreWebhook: (_req_query = req.query) == null ? void 0 : _req_query.ignoreWebhook
        }, {
            parent: parentId,
            child: childId
        });
        const relationType = isMMLike ? _nocodbsdk.RelationTypes.MANY_TO_MANY : colOptions.type;
        switch(relationType){
            case _nocodbsdk.RelationTypes.MANY_TO_MANY:
                {
                    const { vChildCol, vParentCol, assocBaseModel, vTn } = await this.resolveJunctionMeta();
                    // V2 constrained relations (MO, OM, OO): enforce cardinality in a transaction
                    if (isMMLike && colOptions.type !== _nocodbsdk.RelationTypes.MANY_TO_MANY) {
                        await this.enforceV2CardinalityAndInsert({
                            vChildCol,
                            vParentCol,
                            vTn,
                            column,
                            req,
                            refTableLinkColumnId
                        });
                    } else {
                        // Standard MM: just insert, no cardinality enforcement needed
                        if (baseModel.isSnowflake || baseModel.isDatabricks) {
                            const parentPK = parentBaseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first();
                            const childPK = childBaseModel.dbDriver(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first();
                            await baseModel.execAndParse(baseModel.dbDriver.raw(`INSERT INTO ?? (??, ??) SELECT (${parentPK.toQuery()}), (${childPK.toQuery()})`, [
                                vTn,
                                vParentCol.column_name,
                                vChildCol.column_name
                            ]), null, {
                                raw: true
                            });
                        } else {
                            await assocBaseModel.execAndParse(baseModel.dbDriver(vTn).insert({
                                [vParentCol.column_name]: baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first(),
                                [vChildCol.column_name]: baseModel.dbDriver(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first()
                            }), null, {
                                raw: true
                            });
                        }
                        await parentBaseModel.updateLastModified({
                            baseModel: parentBaseModel,
                            model: parentTable,
                            rowIds: [
                                parentId
                            ],
                            cookie: req,
                            updatedColIds: [
                                refTableLinkColumnId
                            ]
                        });
                        await parentBaseModel.broadcastLinkUpdates([
                            parentId
                        ]);
                        await childBaseModel.updateLastModified({
                            baseModel: childBaseModel,
                            model: childTable,
                            rowIds: [
                                childId
                            ],
                            cookie: req,
                            updatedColIds: [
                                column.id
                            ]
                        });
                        await childBaseModel.broadcastLinkUpdates([
                            childId
                        ]);
                    }
                }
                break;
            case _nocodbsdk.RelationTypes.HAS_MANY:
                {
                    const linkedHmRowObj = await this.getHmOrOoChildRow();
                    const oldRowId = linkedHmRowObj ? linkedHmRowObj == null ? void 0 : linkedHmRowObj[childColumn == null ? void 0 : childColumn.column_name] : null;
                    if (oldRowId) {
                        await webhookHandler.addAffectedParentId(oldRowId);
                        const [parentRelatedPkValue, childRelatedPkValue] = await baseModel.readOnlyPrimariesByPkFromModel([
                            {
                                model: childTable,
                                id: childId
                            },
                            {
                                model: parentTable,
                                id: oldRowId
                            }
                        ]);
                        this.auditUpdateObj.push({
                            rowId: oldRowId,
                            refRowId: childId,
                            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                            refDisplayValue: parentRelatedPkValue,
                            displayValue: childRelatedPkValue,
                            direction: 'parent_child',
                            type: colOptions.type
                        });
                        this.auditUpdateObj.push({
                            rowId: childId,
                            refRowId: oldRowId,
                            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                            displayValue: parentRelatedPkValue,
                            refDisplayValue: childRelatedPkValue,
                            direction: 'child_parent',
                            type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type)
                        });
                        await parentBaseModel.updateLastModified({
                            baseModel: parentBaseModel,
                            model: parentTable,
                            rowIds: [
                                oldRowId
                            ],
                            cookie: req,
                            updatedColIds: [
                                column.id
                            ]
                        });
                    }
                    await childBaseModel.execAndParse(baseModel.dbDriver(childTn).update({
                        [childColumn.column_name]: baseModel.dbDriver.from(baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first().as('___cn_alias'))
                    }).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)), null, {
                        raw: true
                    });
                    // await triggerAfterRemoveChild();
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            refTableLinkColumnId
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: [
                            parentId
                        ],
                        cookie: req,
                        updatedColIds: [
                            column.id
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                }
                break;
            case _nocodbsdk.RelationTypes.BELONGS_TO:
                {
                    const linkedHmRowObj = await this.getHmOrOoChildRow();
                    const oldParentRowId = linkedHmRowObj ? linkedHmRowObj[childColumn.column_name] : null;
                    if (oldParentRowId) {
                        var _this1;
                        await webhookHandler.addAffectedParentId(oldParentRowId);
                        const [parentRelatedPkValue, childRelatedPkValue] = await baseModel.readOnlyPrimariesByPkFromModel([
                            {
                                model: parentTable,
                                id: oldParentRowId
                            },
                            {
                                model: childTable,
                                id: childId
                            }
                        ]);
                        this.auditUpdateObj.push({
                            rowId: oldParentRowId,
                            refRowId: childId,
                            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                            displayValue: parentRelatedPkValue,
                            refDisplayValue: childRelatedPkValue,
                            direction: 'parent_child',
                            type: colOptions.type
                        });
                        this.auditUpdateObj.push({
                            rowId: childId,
                            refRowId: oldParentRowId,
                            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                            displayValue: childRelatedPkValue,
                            refDisplayValue: parentRelatedPkValue,
                            direction: 'child_parent',
                            type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type)
                        });
                        await parentBaseModel.updateLastModified({
                            baseModel: parentBaseModel,
                            model: parentTable,
                            rowIds: [
                                oldParentRowId
                            ],
                            cookie: req,
                            updatedColIds: [
                                (_this1 = await (0, _addremovelinks.extractCorrespondingLinkColumn)(childBaseModel.context, {
                                    ltarColumn: column,
                                    referencedTable: parentTable
                                })) == null ? void 0 : _this1.id
                            ]
                        });
                    }
                    await childBaseModel.execAndParse(childBaseModel.dbDriver(childTn).update({
                        [childColumn.column_name]: baseModel.dbDriver.from(baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first().as('___cn_alias'))
                    }).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)), null, {
                        raw: true
                    });
                    // await triggerAfterRemoveChild();
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            column.id
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: [
                            parentId
                        ],
                        cookie: req,
                        updatedColIds: [
                            refTableLinkColumnId
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                }
                break;
            case _nocodbsdk.RelationTypes.ONE_TO_ONE:
                {
                    var _column_meta, _column_meta1;
                    // 1. check current row is linked with another child
                    const linkedCurrentOoRowObj = await this.getHmOrOoChildLinkedWithParent();
                    if (linkedCurrentOoRowObj) {
                        const oldChildRowId = (0, _dbHelpers.getCompositePkValue)(childTable.primaryKeys, baseModel.extractPksValues(linkedCurrentOoRowObj));
                        if (oldChildRowId) {
                            await webhookHandler.addAffectedChildId(oldChildRowId);
                            const [parentRelatedPkValue, childRelatedPkValue] = await baseModel.readOnlyPrimariesByPkFromModel([
                                {
                                    model: childTable,
                                    id: oldChildRowId
                                },
                                {
                                    model: parentTable,
                                    id: parentId
                                }
                            ]);
                            this.auditUpdateObj.push({
                                rowId: parentId,
                                refRowId: oldChildRowId,
                                opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                                displayValue: parentRelatedPkValue,
                                refDisplayValue: childRelatedPkValue,
                                direction: 'parent_child',
                                type: colOptions.type
                            });
                            this.auditUpdateObj.push({
                                rowId: oldChildRowId,
                                refRowId: parentId,
                                opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                                displayValue: childRelatedPkValue,
                                refDisplayValue: parentRelatedPkValue,
                                direction: 'child_parent',
                                type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type)
                            });
                            await childBaseModel.updateLastModified({
                                baseModel: childBaseModel,
                                model: childTable,
                                rowIds: [
                                    oldChildRowId
                                ],
                                cookie: req,
                                updatedColIds: [
                                    column.id
                                ]
                            });
                        }
                    }
                    // 2. check current child is linked with another row cell
                    const linkedOoRowObj = await this.getHmOrOoChildRow();
                    const oldRowId = linkedOoRowObj ? linkedOoRowObj[childColumn.column_name] : null;
                    if (oldRowId) {
                        await webhookHandler.addAffectedParentId(oldRowId);
                        const [parentRelatedPkValue, childRelatedPkValue] = await baseModel.readOnlyPrimariesByPkFromModel([
                            {
                                model: childTable,
                                id: childId
                            },
                            {
                                model: parentTable,
                                id: oldRowId
                            }
                        ]);
                        this.auditUpdateObj.push({
                            rowId: oldRowId,
                            refRowId: childId,
                            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                            displayValue: parentRelatedPkValue,
                            refDisplayValue: childRelatedPkValue,
                            direction: 'parent_child',
                            type: colOptions.type
                        });
                        this.auditUpdateObj.push({
                            rowId: childId,
                            refRowId: oldRowId,
                            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                            displayValue: childRelatedPkValue,
                            refDisplayValue: parentRelatedPkValue,
                            direction: 'child_parent',
                            type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type)
                        });
                        await parentBaseModel.updateLastModified({
                            baseModel: parentBaseModel,
                            model: parentTable,
                            rowIds: [
                                oldRowId
                            ],
                            cookie: req,
                            updatedColIds: [
                                refTableLinkColumnId
                            ]
                        });
                    }
                    // todo: unlink if it's already mapped
                    // unlink already mapped record if any
                    await childBaseModel.execAndParse(baseModel.dbDriver(childTn).where({
                        [childColumn.column_name]: baseModel.dbDriver.from(baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first().as('___cn_alias'))
                    }).update({
                        [childColumn.column_name]: null
                    }), null, {
                        raw: true
                    });
                    await childBaseModel.execAndParse(baseModel.dbDriver(childTn).update({
                        [childColumn.column_name]: baseModel.dbDriver.from(baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first().as('___cn_alias'))
                    }).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)), null, {
                        raw: true
                    });
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            ((_column_meta = column.meta) == null ? void 0 : _column_meta.bt) ? column.id : refTableLinkColumnId
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: parentId,
                        cookie: req,
                        updatedColIds: [
                            ((_column_meta1 = column.meta) == null ? void 0 : _column_meta1.bt) ? refTableLinkColumnId : column.id
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                }
                break;
        }
        this.auditUpdateObj.push({
            rowId: parentId,
            refRowId: childId,
            opSubType: _nocodbsdk.AuditOperationSubTypes.LINK_RECORD,
            type: colOptions.type,
            direction: 'parent_child'
        });
        this.auditUpdateObj.push({
            rowId: childId,
            refRowId: parentId,
            opSubType: _nocodbsdk.AuditOperationSubTypes.LINK_RECORD,
            type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type),
            direction: 'child_parent'
        });
        await webhookHandler.finishUpdate();
    }
    async removeChild(params) {
        var _this, _req_query;
        const { relationColumn, relationColOptions: colOptions, baseModel, parentBaseModel, parentColumn, parentTable, parentTn, childBaseModel, childColumn, childTable, childTn, childId, parentId, mmContext } = this.relationContext;
        const column = relationColumn;
        const { req } = params;
        // Resolve once — every branch uses the same column + table
        const refTableLinkColumnId = (_this = await (0, _addremovelinks.extractCorrespondingLinkColumn)(childBaseModel.context, {
            ltarColumn: relationColumn,
            referencedTable: parentTable
        })) == null ? void 0 : _this.id;
        const webhookHandler = await _relationupdatewebhookhandler.RelationUpdateWebhookHandler.beginUpdate({
            childBaseModel,
            parentBaseModel,
            user: req.user,
            ignoreWebhook: (_req_query = req.query) == null ? void 0 : _req_query.ignoreWebhook
        }, {
            parent: parentId,
            child: childId
        });
        const relationType = (0, _nocodbsdk.isMMOrMMLike)(relationColumn) ? _nocodbsdk.RelationTypes.MANY_TO_MANY : colOptions.type;
        switch(relationType){
            case _nocodbsdk.RelationTypes.MANY_TO_MANY:
                {
                    const vChildCol = await colOptions.getMMChildColumn(mmContext);
                    const vParentCol = await colOptions.getMMParentColumn(mmContext);
                    const vTable = await colOptions.getMMModel(mmContext);
                    const assocBaseModel = await _models.Model.getBaseModelSQL(mmContext, {
                        model: vTable,
                        dbDriver: baseModel.dbDriver
                    });
                    const vTn = assocBaseModel.getTnPath(vTable);
                    await assocBaseModel.execAndParse(baseModel.dbDriver(vTn).where({
                        [vParentCol.column_name]: baseModel.dbDriver(parentTn).select(parentColumn.column_name).where((0, _dbHelpers._wherePk)(parentTable.primaryKeys, parentId)).first(),
                        [vChildCol.column_name]: baseModel.dbDriver(childTn).select(childColumn.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).first()
                    }).delete(), null, {
                        raw: true
                    });
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: [
                            parentId
                        ],
                        cookie: req,
                        updatedColIds: [
                            refTableLinkColumnId
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            colOptions.fk_column_id
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                }
                break;
            case _nocodbsdk.RelationTypes.HAS_MANY:
                {
                    await childBaseModel.execAndParse(baseModel.dbDriver(childTn)// .where({
                    //   [childColumn.cn]: this.dbDriver(parentTable.tn)
                    //     .select(parentColumn.cn)
                    //     .where(parentTable.primaryKey.cn, rowId)
                    //     .first()
                    // })
                    .where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).update({
                        [childColumn.column_name]: null
                    }), null, {
                        raw: true
                    });
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            colOptions.fk_column_id
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: [
                            parentId
                        ],
                        cookie: req,
                        updatedColIds: [
                            refTableLinkColumnId
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                }
                break;
            case _nocodbsdk.RelationTypes.BELONGS_TO:
                {
                    await childBaseModel.execAndParse(baseModel.dbDriver(childTn)// .where({
                    //   [childColumn.cn]: this.dbDriver(parentTable.tn)
                    //     .select(parentColumn.cn)
                    //     .where(parentTable.primaryKey.cn, childId)
                    //     .first()
                    // })
                    .where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).update({
                        [childColumn.column_name]: null
                    }), null, {
                        raw: true
                    });
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            column.id
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: [
                            parentId
                        ],
                        cookie: req,
                        updatedColIds: [
                            refTableLinkColumnId
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                }
                break;
            case _nocodbsdk.RelationTypes.ONE_TO_ONE:
                {
                    await childBaseModel.execAndParse(baseModel.dbDriver(childTn).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, childId)).update({
                        [childColumn.column_name]: null
                    }), null, {
                        raw: true
                    });
                    await childBaseModel.updateLastModified({
                        baseModel: childBaseModel,
                        model: childTable,
                        rowIds: [
                            childId
                        ],
                        cookie: req,
                        updatedColIds: [
                            colOptions.fk_column_id
                        ]
                    });
                    await childBaseModel.broadcastLinkUpdates([
                        childId
                    ]);
                    await parentBaseModel.updateLastModified({
                        baseModel: parentBaseModel,
                        model: parentTable,
                        rowIds: [
                            parentId
                        ],
                        cookie: req,
                        updatedColIds: [
                            refTableLinkColumnId
                        ]
                    });
                    await parentBaseModel.broadcastLinkUpdates([
                        parentId
                    ]);
                }
                break;
        }
        this.auditUpdateObj.push({
            rowId: parentId,
            refRowId: childId,
            opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
            type: colOptions.type,
            direction: 'parent_child'
        });
        if (parentTable.id !== childTable.id) {
            this.auditUpdateObj.push({
                rowId: childId,
                refRowId: parentId,
                opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type),
                direction: 'child_parent'
            });
        }
        await webhookHandler.finishUpdate();
    }
    async handleOnlyUpdateAudit(params) {
        const { relationColumn: column, relationColOptions: colOptions, baseModel, parentTable, childTable, childId, parentId } = this.relationContext;
        const { prevData } = params;
        const oldChildRowId = prevData[column.title] ? (0, _dbHelpers.getCompositePkValue)(parentTable.primaryKeys, baseModel.extractPksValues(prevData[column.title])) : null;
        const [childRelatedPkValue] = await baseModel.readOnlyPrimariesByPkFromModel([
            {
                model: childTable,
                id: childId
            }
        ]);
        if (oldChildRowId) {
            var _ref, _ref1;
            var _prevData_column_title, _prevData_column_title1;
            this.auditUpdateObj.push({
                rowId: parentId,
                refRowId: oldChildRowId,
                opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                displayValue: (_ref = (_prevData_column_title = prevData[column.title]) == null ? void 0 : _prevData_column_title[parentTable.displayValue.title]) != null ? _ref : null,
                refDisplayValue: childRelatedPkValue,
                direction: 'parent_child',
                type: colOptions.type
            });
            this.auditUpdateObj.push({
                rowId: oldChildRowId,
                refRowId: parentId,
                opSubType: _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD,
                displayValue: childRelatedPkValue,
                refDisplayValue: (_ref1 = (_prevData_column_title1 = prevData[column.title]) == null ? void 0 : _prevData_column_title1[parentTable.displayValue.title]) != null ? _ref1 : null,
                direction: 'child_parent',
                type: (0, _dbHelpers.getOppositeRelationType)(colOptions.type)
            });
        }
    }
    getAuditUpdateObj(req) {
        const { childTable, parentTable, parentColumn, childColumn } = this.relationContext;
        return this.auditUpdateObj.map((log)=>{
            const column = log.direction === 'parent_child' ? parentColumn : childColumn;
            const refColumn = log.direction === 'parent_child' ? childColumn : parentColumn;
            return _extends({}, log, {
                model: log.direction === 'parent_child' ? parentTable : childTable,
                refModel: log.direction === 'parent_child' ? childTable : parentTable,
                columnTitle: column.title,
                refColumnTitle: refColumn.title,
                columnId: column.id,
                req
            });
        });
    }
    constructor(relationContext){
        this.relationContext = relationContext;
        this.auditUpdateObj = [];
    }
};
